import {showNotification} from 'platform/components';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {
  useLazyGetMinimumMarginCustomRecalculationQuery,
  usePatchMinimumMarginCurrentSettingsMutation,
} from '@dms/api/workshop';
import {getPercentageFromDecimal, handleApiError} from '@dms/shared';

import {DiscountSectionFormType} from '../types/discountSectionSchema';

interface UseApplyCurrentMinimumMarginSettingsProps {
  serviceCaseId: string;
  serviceOrderId: string;
  formApi: UseFormReturn<DiscountSectionFormType>;
}

export function useApplyCurrentMinimumMarginSettings(
  props: UseApplyCurrentMinimumMarginSettingsProps
) {
  // Spans the whole PATCH -> refetch chain; the mutation's own isLoading drops to false
  // while the refetch is still running, which would let a second click through.
  const [isApplyingCurrentSettings, setIsApplyingCurrentSettings] = useState(false);

  const [patchCurrentSettings] = usePatchMinimumMarginCurrentSettingsMutation();
  // The PATCH invalidates the margin tag, so the section's query refetches on its own. This
  // lazy read exists only because react-hook-form holds its own copy of the field values and
  // will not pick the refreshed margins up from the query.
  const [getMinimumMargin] = useLazyGetMinimumMarginCustomRecalculationQuery();

  const applyCurrentSettings = async () => {
    if (isApplyingCurrentSettings) {
      return;
    }

    setIsApplyingCurrentSettings(true);

    await patchCurrentSettings({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    })
      .unwrap()
      .then(() =>
        getMinimumMargin({
          serviceCaseId: props.serviceCaseId,
          serviceOrderId: props.serviceOrderId,
        }).unwrap()
      )
      .then((minimumMargin) => {
        props.formApi.setValue('isMinimumMarginExpanded', minimumMargin?.isCustomEnabled ?? false);
        props.formApi.setValue(
          'minimumMargin.material',
          getPercentageFromDecimal(minimumMargin?.customMaterialMargin) ?? null
        );
        props.formApi.setValue(
          'minimumMargin.labour',
          getPercentageFromDecimal(minimumMargin?.customLabourMargin) ?? null
        );
        showNotification.success();
      })
      .catch(handleApiError)
      .finally(() => setIsApplyingCurrentSettings(false));
  };

  return {applyCurrentSettings, isApplyingCurrentSettings};
}
